export const ROLE_STORAGE_KEY = 'ihsanone_cms_role';

export const CMS_ROLES = [
  { id: 'member', label: 'Member' },
  { id: 'duty_officer', label: 'Duty officer' },
  { id: 'asatizah', label: 'Asatizah' },
  { id: 'staff_learning', label: 'Learning staff' },
];

export function getDirectusUrl() {
  // Self-hosted Directus (GCP Cloud Run) — public catalogue reads need no token.
  // Baked default mirrors session.js's GoTrue URL so a plain CLI build works;
  // REACT_APP_DIRECTUS_URL still overrides per-environment.
  return (process.env.REACT_APP_DIRECTUS_URL
    || 'https://directus-jgmy2ghs6a-as.a.run.app').replace(/\/$/, '');
}

export function tokenForRole(role) {
  const map = {
    member: process.env.REACT_APP_DIRECTUS_TOKEN_MEMBER || '',
    duty_officer: process.env.REACT_APP_DIRECTUS_TOKEN_DUTY_OFFICER || '',
    asatizah: process.env.REACT_APP_DIRECTUS_TOKEN_ASATIZAH || '',
    staff_learning: process.env.REACT_APP_DIRECTUS_TOKEN_STAFF_LEARNING || '',
    public: '',
  };
  return map[role] ?? map.member;
}

// Staff cards live behind a token. A deployed build ships none, so offering
// those roles there would be a switcher that silently shows nothing.
export const AVAILABLE_ROLES = CMS_ROLES.filter(
  (role) => role.id === 'member' || tokenForRole(role.id),
);

export function readStoredRole() {
  try {
    const value = window.localStorage.getItem(ROLE_STORAGE_KEY);
    if (AVAILABLE_ROLES.some((role) => role.id === value)) return value;
  } catch {
    /* private mode */
  }
  return 'member';
}

export function storeRole(role) {
  try {
    window.localStorage.setItem(ROLE_STORAGE_KEY, role);
  } catch {
    /* private mode */
  }
}

function parseJson(value, fallback) {
  if (value == null || value === '') return fallback;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

export function mapDirectusRow(row) {
  const extra = parseJson(row.extra, {}) || {};
  return {
    ...extra,
    id: row.slug || extra.id,
    title: row.title || row.title_en || extra.title,
    org: row.org || extra.org,
    date: row.date_label || extra.date,
    tag: row.tag || extra.tag,
    tagColor: row.tag_color || extra.tagColor,
    image: row.cover_url || extra.image,
    description: row.summary || row.summary_en || extra.description,
    location: row.location_text || extra.location,
    facts: parseJson(row.facts, extra.facts),
    highlights: parseJson(row.highlights, extra.highlights),
    staffOnly: Boolean(row.staff_only || extra.staffOnly),
  };
}

export function mapHeroRow(row) {
  const extra = parseJson(row.extra, {}) || {};
  return {
    id: extra.id || row.slug,
    title: row.title || row.title_en || extra.title,
    subtitle: extra.subtitle || row.summary || row.summary_en,
    image: row.cover_url || extra.image,
  };
}

async function getItems(url, collection, token, { sorted = true } = {}) {
  const headers = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  // Sorting by a column the collection doesn't have is a 400, so opt out for
  // collections that carry no `sort` field.
  const query = sorted ? '?limit=-1&sort=sort' : '?limit=-1';
  const res = await fetch(`${url}/items/${collection}${query}`, { headers });
  if (!res.ok) return [];
  const json = await res.json();
  return json.data || [];
}

// Opt-in remote flags. Fetching `app_flags` when the collection isn't granted
// to the public role prints a red "Failed to load resource: 403" in every
// visitor's console on load — a launch smell — even though the code below
// swallows it gracefully. So the fetch is off by default and turns on only
// when the collection is known to exist + be public. Env override lets us
// flip it back on without a code change once Directus is provisioned.
const REMOTE_FLAGS_ENABLED = (() => {
  const raw = process.env.REACT_APP_REMOTE_FLAGS;
  return raw === 'true' || raw === '1';
})();

export async function fetchDirectusCatalog(role) {
  const url = getDirectusUrl();
  if (!url) return null;
  const token = tokenForRole(role);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 25000);
  try {
    const ping = await fetch(`${url}/server/ping`, { signal: controller.signal });
    if (!ping.ok) return null;
    const catalogFetches = [
      getItems(url, 'listings', token),
      getItems(url, 'app_modules', token),
    ];
    if (REMOTE_FLAGS_ENABLED) {
      catalogFetches.push(
        getItems(url, 'app_flags', token, { sorted: false }).catch(() => []),
      );
    } else {
      catalogFetches.push(Promise.resolve([]));
    }
    const [listingRows, moduleRows, flagRows] = await Promise.all(catalogFetches);
    return { listingRows, moduleRows, flagRows };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
